import { useEffect, useState } from "react";
import Spinner from "./../ui/Spinner";
import { ErrorState, Help, HowItWorks, PersonCell, btnGhost, btnPrimary, errMsg, inputCls } from "./promotionsUi";
import { SKIP_LABEL, Stat, type Toast } from "./campaignUi";
import { promotionsService } from "../../features/promotions/service";
import { formatISTDateTime } from "../../utils/dateUtils";
import type { Campaign, RecipientRow, RecipientsFilter, RecipientsSummary } from "../../features/promotions/types";

const RECIPIENTS_PAGE = 50;

// ── Recipients & acknowledgements drawer ────────────────────────────────────
const RSTATUS_BADGE: Record<string, string> = {
  granted: "bg-emerald-900/60 text-emerald-300",
  pending: "bg-blue-900/60 text-blue-300",
  skipped: "bg-gray-700 text-gray-300",
  failed: "bg-red-900/50 text-red-300",
};

export function RecipientsDrawer({ campaign, onClose, toast }: { campaign: Campaign; onClose: () => void; toast: Toast }) {
  const [summary, setSummary] = useState<RecipientsSummary | null>(null);
  const [filter, setFilter] = useState<RecipientsFilter>({ status: "", acknowledged: "", source: "", q: "" });
  const [qInput, setQInput] = useState("");
  const [rows, setRows] = useState<RecipientRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    promotionsService.recipientsSummary(campaign.id).then(setSummary).catch((e) => toast.showError(errMsg(e, "Failed to load summary")));
  }, [campaign.id, toast]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setFilter((f) => ({ ...f, q: qInput })); }, 400);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setListError(null);
    promotionsService
      .listRecipients(campaign.id, filter, RECIPIENTS_PAGE, page * RECIPIENTS_PAGE)
      .then((r) => { if (!cancelled) { setRows(r.items); setTotal(r.total); } })
      .catch((e) => { if (!cancelled) { setRows([]); setListError(errMsg(e, "Failed to load recipients")); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [campaign.id, filter, page, reloadTick]);

  const setF = (p: Partial<RecipientsFilter>) => { setPage(0); setFilter((f) => ({ ...f, ...p })); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      await promotionsService.exportRecipients(campaign.id, filter, `campaign_${campaign.name.replace(/[^A-Za-z0-9_-]+/g, "_")}_recipients.csv`);
    } catch (e) {
      toast.showError(errMsg(e, "Failed to export CSV"));
    } finally {
      setExporting(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / RECIPIENTS_PAGE));
  const cards: [string, React.ReactNode, string?][] = summary
    ? [
        ["Targeted", summary.targeted],
        ["Granted", summary.granted, "text-emerald-400"],
        ["Pending", summary.pending, "text-blue-300"],
        ["Skipped", summary.skipped],
        ["Failed", summary.failed, summary.failed ? "text-red-300" : undefined],
        ["Notified", summary.notified],
        ["Acknowledged (of granted + pending)", `${summary.acknowledged} (${Number(summary.acknowledged_pct ?? 0).toFixed(1)}%)`, "text-amber-300"],
      ]
    : [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-gray-900 border-l border-gray-800 w-full max-w-5xl h-full overflow-y-auto p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-semibold text-lg">Recipients and acknowledgements</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none" aria-label="Close">×</button>
        </div>
        <p className="text-gray-500 text-xs mb-3">{campaign.name}. Times are IST.</p>
        <div className="mb-4">
          <HowItWorks>
            <p>Targeted = everyone the campaign picked. Granted = credits added. Pending = waiting for the user to claim. Skipped = not eligible (see reason). Failed = grant error, press Apply again to retry.</p>
            <p>Notified = a notice was sent. Acknowledged = the user tapped Acknowledge or Claim (or "Got it" on the welcome popup for onboarding recipients). The CSV export uses the filters currently chosen.</p>
          </HowItWorks>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
          {cards.map(([label, value, tone]) => <Stat key={label} label={label} value={value} tone={tone} />)}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-3">
          <select className={inputCls} value={filter.status} onChange={(e) => setF({ status: e.target.value as RecipientsFilter["status"] })} aria-label="Status">
            <option value="">All statuses</option>
            <option value="granted">Granted</option>
            <option value="pending">Pending</option>
            <option value="skipped">Skipped</option>
            <option value="failed">Failed</option>
          </select>
          <select className={inputCls} value={filter.acknowledged} onChange={(e) => setF({ acknowledged: e.target.value as RecipientsFilter["acknowledged"] })} aria-label="Acknowledged">
            <option value="">Acknowledged: any</option>
            <option value="true">Acknowledged: yes</option>
            <option value="false">Acknowledged: no</option>
          </select>
          <select className={inputCls} value={filter.source} onChange={(e) => setF({ source: e.target.value as RecipientsFilter["source"] })} aria-label="Source">
            <option value="">Any source</option>
            <option value="existing">Existing user</option>
            <option value="onboarding">Onboarding</option>
          </select>
          <input className={`${inputCls} lg:col-span-1`} value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search name, email, company" />
          <button onClick={exportCsv} disabled={exporting} className={btnPrimary}>{exporting ? "Exporting…" : "Export CSV"}</button>
        </div>
        <Help>Filter by status, acknowledgement or source, or search by name, email or company. Export CSV downloads the filtered list.</Help>

        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                {["Name / email", "Role", "Company", "Source", "Status", "Credits", "Skip reason", "Notified", "Granted", "Acknowledged"].map((h) => (
                  <th key={h} className="py-2 pr-3 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-b border-gray-800/60 text-gray-300 align-top">
                  <td className="py-2 pr-3 min-w-[10rem]"><PersonCell person={r} fallback="Deleted user" /></td>
                  <td className="py-2 pr-3 capitalize">{r.role || "—"}</td>
                  <td className="py-2 pr-3">{r.company || "—"}</td>
                  <td className="py-2 pr-3 capitalize">{r.source}</td>
                  <td className="py-2 pr-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${RSTATUS_BADGE[r.status] ?? RSTATUS_BADGE.skipped}`}>{r.status}</span></td>
                  <td className="py-2 pr-3">{r.credits}</td>
                  <td className="py-2 pr-3 text-xs">{r.skip_reason ? (SKIP_LABEL[r.skip_reason] ?? r.skip_reason) : "—"}</td>
                  <td className="py-2 pr-3 text-xs whitespace-nowrap">{formatISTDateTime(r.notified_at)}</td>
                  <td className="py-2 pr-3 text-xs whitespace-nowrap">{formatISTDateTime(r.granted_at)}</td>
                  <td className="py-2 pr-3 text-xs whitespace-nowrap">{formatISTDateTime(r.acknowledged_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && <div className="flex justify-center py-6"><Spinner /></div>}
        {listError && <ErrorState message={listError} onRetry={() => setReloadTick((n) => n + 1)} />}
        {!loading && !listError && rows.length === 0 && (
          <p className="text-gray-500 text-sm py-6 text-center">
            {filter.status || filter.acknowledged || filter.source || filter.q ? "No recipients match these filters." : "Nobody has been targeted yet. Preview the audience and press Apply to create recipients."}
          </p>
        )}

        <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
          <span>{total.toLocaleString("en-IN")} total</span>
          <div className="flex items-center gap-2">
            <button className={btnGhost} disabled={page === 0 || loading} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>Page {page + 1} of {pages}</span>
            <button className={btnGhost} disabled={page + 1 >= pages || loading} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}